import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { AccountSelectors, CurrentUserSelectors } from '@core/account-data';
import { User } from '@core/account-types';
import { AccessToCandidatePolicy, FullCandidateSelectors } from '@core/candidate-data';
import { DevFlagSelectors } from '@core/dev-flags-data';
import { DevFlag } from '@core/dev-flags-types';
import { FeatureSpecFacade } from '@core/feature-spec-logic';
import { FeatureCode } from '@core/feature-spec-types';
import { IntegrationsInsertsService } from '@core/integrations-logic';
import { TriggerCategory } from '@core/workflow-automations-types';
import { AutomationsJobItem } from '@recruitee/workflow-automations-types';

@Injectable()
export class AutomationsAccessoryDataService {
  private readonly store = inject(Store);
  private readonly integrationsInsertsService = inject(IntegrationsInsertsService);
  private readonly featureSpecFacade = inject(FeatureSpecFacade);

  public hasKnockoutQuestionsEntitled = this.featureSpecFacade.selectIsEntitled(
    FeatureCode.KnockoutQuestions,
  );
  public hasWorkflowAutomationImprovementsFlag = this.store.selectSignal(
    DevFlagSelectors.isEnabled(DevFlag.WorkflowAutomationImprovements),
  );

  public isLockedStage = signal(false);
  public shareCandidateUsersData = this.store.selectSignal(
    FullCandidateSelectors.groupedUsersInShareActionContext({
      atcPolicy: AccessToCandidatePolicy.NoCandidateViewAbility,
    }),
  );
  public allowedUsers = computed(() => this.groupedUsers()[this._triggerCategory()].allowedUsers);
  public disallowedUsers = computed(
    () => this.groupedUsers()[this._triggerCategory()].disallowedUsers,
  );
  public jobItemData = signal<AutomationsJobItem | null>(null);
  public currentUser = this.store.selectSignal(CurrentUserSelectors.data);
  public enabledIntegrations = toSignal(
    this.integrationsInsertsService.getEnabledIntegrationKids(),
  );
  public companyName = this.store.selectSignal(AccountSelectors.currentCompanyName);

  private groupedUsers = signal<
    Record<TriggerCategory, { allowedUsers: User[]; disallowedUsers: User[] }>
  >({
    [TriggerCategory.Candidate]: { allowedUsers: [], disallowedUsers: [] },
    [TriggerCategory.Job]: { allowedUsers: [], disallowedUsers: [] },
  });
  private _triggerCategory = signal<TriggerCategory>(TriggerCategory.Candidate);

  public setGroupedUsers(
    users: Record<TriggerCategory, { allowedUsers: User[]; disallowedUsers: User[] }>,
  ): void {
    this.groupedUsers.set(users);
  }

  public setTriggerCategory(category: TriggerCategory): void {
    this._triggerCategory.set(category);
  }

  public get triggerCategory() {
    return this._triggerCategory.asReadonly();
  }

  public setJobItemData(jobData: AutomationsJobItem | null): void {
    this.jobItemData.set(jobData);
  }
}
